import { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Table, TableHead, TableRow,
    TableCell, TableBody, Chip, CircularProgress, Alert, Stack, Button,
    Drawer, IconButton, List, ListItem, ListItemText,
} from '@mui/material';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import syndicationApi from '../../utils/syndicationApi';
import SyndicationDocumentViewer from '../../components/syndication/SyndicationDocumentViewer';

export default function InvestorPortfolio() {
    const [portfolio, setPortfolio] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Post-investment detail drawer
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => {
        fetchPortfolio();
    }, []);

    const fetchPortfolio = () => {
        setLoading(true);
        syndicationApi.get('investor/portfolio/')
            .then(res => {
                setPortfolio(res.data.data || []);
                setError('');
            })
            .catch(err => {
                setError(err.response?.data?.message || 'Failed to load portfolio.');
            })
            .finally(() => setLoading(false));
    };

    const openPostInvestmentSpace = (dealId) => {
        setDetailOpen(true);
        setDetailLoading(true);
        syndicationApi.get(`investor/portfolio/${dealId}/`)
            .then(res => setDetailData(res.data.data || null))
            .catch(() => setDetailData(null))
            .finally(() => setDetailLoading(false));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Portfolio</Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {portfolio.length === 0 ? (
                <Alert severity="info">
                    No closed deals in your portfolio yet. Once your allocation is confirmed on a deal, it will appear here.
                </Alert>
            ) : (
                <Card>
                    <CardContent sx={{ p: 0 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Deal</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Instrument</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Committed</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Allocated</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {portfolio.map((item) => (
                                    <TableRow key={item.deal_id} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{item.deal_code}</Typography>
                                        </TableCell>
                                        <TableCell>{item.company_name}</TableCell>
                                        <TableCell>
                                            <Chip label={item.instrument_type} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            {item.currency ? `${item.currency} ` : ''}{Number(item.committed_amount).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
                                                {item.currency ? `${item.currency} ` : ''}{Number(item.allocated_amount).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={item.decision}
                                                size="small"
                                                color={item.decision === 'binding' ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {item.has_post_investment_space && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => openPostInvestmentSpace(item.deal_id)}
                                                >
                                                    View Space
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Post-Investment Space Drawer */}
            <Drawer
                anchor="right"
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                PaperProps={{ sx: { width: { xs: '100%', md: 560 } } }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Post-Investment Space</Typography>
                        <IconButton onClick={() => setDetailOpen(false)} size="small">
                            <Icon icon={closeFill} />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                        {detailLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                        ) : !detailData ? (
                            <Alert severity="info">Post-investment space not yet available.</Alert>
                        ) : (
                            <Stack spacing={3}>
                                {/* Deal Summary */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Deal Summary</Typography>
                                        <Typography variant="body2">Deal: <strong>{detailData.deal_title}</strong> ({detailData.deal_code})</Typography>
                                        <Typography variant="body2">Your Allocation: <strong>{Number(detailData.your_allocation).toLocaleString()}</strong></Typography>
                                        <Typography variant="body2">Your Commitment: {Number(detailData.your_commitment).toLocaleString()} ({detailData.your_decision})</Typography>
                                    </CardContent>
                                </Card>

                                {/* Legal Documents */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                            Legal & Governance Documents ({detailData.documents?.length || 0})
                                        </Typography>
                                        {detailData.documents && detailData.documents.length > 0 ? (
                                            <List dense disablePadding>
                                                {detailData.documents.map(doc => (
                                                    <ListItem key={doc.id} divider secondaryAction={
                                                        <SyndicationDocumentViewer
                                                            fileUrl={doc.file}
                                                            fileType={doc.file_type}
                                                            title={doc.title}
                                                            showDownload={false}
                                                            iconOnly
                                                            size="small"
                                                        />
                                                    }>
                                                        <ListItemText
                                                            primary={doc.title}
                                                            secondary={`${doc.category} | ${doc.file_type} | ${(doc.file_size / 1024).toFixed(1)} KB${doc.requires_signature ? ' | Signature Required' : ''}`}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">No documents uploaded yet.</Typography>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Investor Updates */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                            Investor Updates ({detailData.updates?.length || 0})
                                        </Typography>
                                        {detailData.updates && detailData.updates.length > 0 ? (
                                            <Stack spacing={2}>
                                                {detailData.updates.map(upd => (
                                                    <Card key={upd.id} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{upd.title}</Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                {upd.update_type} | {upd.published_at ? new Date(upd.published_at).toLocaleDateString() : 'Draft'}
                                                            </Typography>
                                                            <Typography variant="body2" dangerouslySetInnerHTML={{ __html: upd.content }} />
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">No updates published yet.</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Stack>
                        )}
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
}
